import { useContext, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { ChatContext, Lobby, Message, MilestoneDiscussion } from '@fable/chat'

export const useClubChatNavigation = () => {
  const pendingCloseThread = useRef(false)
  const pendingOpenThread = useRef(false)
  const location = useLocation()
  const {
    milestoneDiscussionsQuery,
    currentRoom,
    messagesQuery,
    openedThreads,
    enterRoom,
    closeThread,
  } = useContext(ChatContext)

  const milestonesQueryData = milestoneDiscussionsQuery?.data?.data
  const lobby = milestonesQueryData?.lobby as Lobby
  const milestones = milestonesQueryData?.milestones as MilestoneDiscussion[]
  const openedThread = (openedThreads || [])[0]

  const params = new URLSearchParams(location.search)
  const roomIdParam = params.get('rid')
  const threadParentTypeParam = params.get('tpt')
  const threadParentIdParam = params.get('tpid')
  const threadParentContentIdParam = params.get('tpcid')

  // The chat package does not rely on navigation because each client which uses it
  // could have different specifications and navigation rules
  // But to allow deep linking to threads and rooms, and use of back button, navigation must be added

  const getRoomUrl = (roomId?: string) => `${location.pathname}?rid=${roomId}`

  const getThreadUrl = (threadParent: Message) =>
    `${location.pathname}?rid=${roomIdParam}&tpt=${threadParent.type}&tpid=${threadParent.id}&tpcid=${threadParent.content_id}`

  useEffect(() => {
    // When changing rooms via ClubChatNav, enter room
    if (roomIdParam && currentRoom?.room_id !== roomIdParam) {
      let room = null

      if (lobby?.room_id === roomIdParam) {
        room = lobby
      } else {
        for (let i = 0; i < milestones?.length || 0; i++) {
          const rooms = milestones[i].rooms

          for (let j = 0; j < rooms.length; j++) {
            if (rooms[j].room_id === roomIdParam) {
              room = rooms[j]

              break
            }
          }
        }
      }

      if (room) {
        enterRoom({ room })
      }
    }
  }, [currentRoom?.room_id, enterRoom, lobby, milestones, roomIdParam])

  useEffect(() => {
    if (!openedThread && !!threadParentIdParam && !pendingOpenThread.current) {
      pendingOpenThread.current = true

      let page = 0
      const pages = messagesQuery?.data?.pages || []
      const threadParent = pages[page]?.results?.find(
        (result: Message) => result.id === threadParentIdParam
      )
      if (!!threadParent) {
        enterRoom({ room: currentRoom, threadParent })
      } else {
        // If the thread is not in the current page, keep searching
        messagesQuery?.fetchNextPage()
      }
    }
  }, [currentRoom, enterRoom, messagesQuery, openedThread, threadParentIdParam])

  useEffect(() => {
    if (pendingOpenThread.current && !!openedThread) {
      pendingOpenThread.current = false
    }
  }, [openedThread])

  useEffect(() => {
    if (!threadParentIdParam && openedThread && !pendingCloseThread.current) {
      pendingCloseThread.current = true
      closeThread()
    }
  }, [closeThread, openedThread, threadParentIdParam])

  return {
    roomIdParam,
    threadParentTypeParam,
    threadParentIdParam,
    threadParentContentIdParam,
    getRoomUrl,
    getThreadUrl,
  }
}
